'use client';

import { withAuth } from '@/HOC/withAuth';
import NoWorkspaceGuard from '@/components/NoWorkspaceGuard';
import RemoveChatbotGuard from '@/components/RemoveChatbotGuard';
import ViewerRouteGuard from '@/components/ViewerRouteGuard';
import { ProtectedLayout } from '@/components/layouts/ProtectedLayout';
import { CommandPaletteProvider } from '@/components/command-palette/CommandPaletteProvider';
import { NotificationProvider } from '@/contexts/NotificationProvider';
import { AnnouncementsProvider } from '@/contexts/AnnouncementsProvider';
import BotInboxNotifier from '@/components/BotInboxNotifier';
import OutreachRealtime from '@/components/OutreachRealtime';
import OnboardingSignOut from '@/components/onboarding/OnboardingSignOut';
import { useMountLogger } from '@/utils/useMountLogger';
import * as React from 'react';

interface ILayoutProps {
  children: React.ReactNode;
}

// PostHog provider + init + identify + pageviews now live in the root-level
// AnalyticsProvider (app/layout.tsx) so the funnel is continuous across public,
// auth, and app routes. This layout no longer mounts its own PostHog wiring.
const Layout: React.FunctionComponent<ILayoutProps> = ({ children }) => {
  // This is the Component withAuth() wraps -- a mount log here directly proves
  // (or disproves) a withAuth-driven unmount/remount of the whole private tree.
  useMountLogger('(private)/layout.Layout');
  return (
    <NotificationProvider>
      <AnnouncementsProvider>
        <BotInboxNotifier />
        <OutreachRealtime />
        <CommandPaletteProvider>
          <ProtectedLayout>
            <OnboardingSignOut />
            <NoWorkspaceGuard />
            <ViewerRouteGuard />
            <RemoveChatbotGuard>{children}</RemoveChatbotGuard>
          </ProtectedLayout>
        </CommandPaletteProvider>
      </AnnouncementsProvider>
    </NotificationProvider>
  );
};

export default withAuth(Layout);
